"use client";
/**
 * Priser / Pricing — Svendetid marketing pricing page content.
 * Recreated from the Claude Design handoff "Priser.dc.html".
 *
 * Language-parametrized so /pricing (da) and /en/pricing (en) share one source.
 */
import Link from "next/link";
import { SignUpButton } from "@clerk/nextjs";
import {
  dsFontClass, dsTokensCss, Icon, Footer, type Lang,
} from "./svendetid-ds";
import LandingNav from "./landing-nav";

const T = {
  da: {
    badge: "Gratis prøveperiode · ingen binding",
    h1: "Enkel pris. Pr. bruger, pr. måned.",
    sub: "Start gratis i dag. Vælg Pro til den daglige drift, eller Business+ når du vil have SvendAI til at gøre papirarbejdet for dig.",
    proLabel: "Pro-licens",
    proTitle: "Til den daglige drift",
    perUser: "pr. bruger / måned",
    cta: "Kom i gang – det er gratis",
    proIncludes: "Indeholder:",
    storagePro: "lagerplads pr. bruger",
    bizTitlePrefix: "Pro + ",
    bizIncludes: "Alt i Pro — og dertil:",
    proItems: [
      "Tidsregistrering", "Fakturaopfølgning", "Omkostningsstyring & rapportering",
      "Job- og tilbudsstyring", "Kvalitetskontrolmodul", "Mobilapp (iOS & Android)",
    ],
    quickstart: "Lynstart af tilbud",
    quickstartBody: " — SvendAI opretter tilbud med pris- og tidsestimat og finder tid i kalenderen",
    bizItems: ["SvendAI chat-agent", "AI-omskrivning", "Automatisk e-mailudsendelse", "AI-fakturascanning"],
    storageBizPrefix: "Op til ", storageBiz: " lagerplads pr. bruger",
    fineprint: "Priserne er ekskl. moms · betales månedligt · opsig når som helst",
    compare: "Sammenlign planer",
    drift: "Daglig drift",
    storageRow: "Lagerplads pr. bruger",
    compareDrift: [
      "Tidsregistrering", "Fakturaopfølgning", "Omkostningsstyring & rapportering",
      "Job- og tilbudsstyring", "Kvalitetskontrolmodul", "Mobilapp (iOS & Android)",
    ],
    compareAi: ["Lynstart af tilbud", "SvendAI chat-agent", "AI-omskrivning", "Automatisk e-mailudsendelse", "AI-fakturascanning"],
    ctaRowNote: "Gratis at komme i gang · ingen binding",
    choosePro: "Vælg Pro", chooseBiz: "Vælg Business+",
    perMonth: "/ md.",
    bandTitle: "Prøv det gratis — uden binding",
    bandBody: "Kom i gang i dag, og opgrader til Pro eller Business+, når det passer dig.",
    seeFeatures: "Se alle funktioner",
  },
  en: {
    badge: "Free trial · no commitment",
    h1: "Simple pricing. Per user, per month.",
    sub: "Start free today. Choose Pro for day-to-day operations, or Business+ when you want SvendAI to handle the paperwork for you.",
    proLabel: "Pro licence",
    proTitle: "For day-to-day operations",
    perUser: "per user / month",
    cta: "Get started – it's free",
    proIncludes: "Includes:",
    storagePro: "storage per user",
    bizTitlePrefix: "Pro + ",
    bizIncludes: "Everything in Pro — plus:",
    proItems: [
      "Time tracking", "Invoice follow-up", "Cost management & reporting",
      "Job & quote management", "Quality control module", "Mobile app (iOS & Android)",
    ],
    quickstart: "Quick-start quotes",
    quickstartBody: " — SvendAI creates quotes with price and time estimates and finds a slot in your calendar",
    bizItems: ["SvendAI chat agent", "AI rewriting", "Automatic email sending", "AI invoice scanning"],
    storageBizPrefix: "Up to ", storageBiz: " storage per user",
    fineprint: "Prices exclude VAT · billed monthly · cancel anytime",
    compare: "Compare plans",
    drift: "Day-to-day",
    storageRow: "Storage per user",
    compareDrift: [
      "Time tracking", "Invoice follow-up", "Cost management & reporting",
      "Job & quote management", "Quality control module", "Mobile app (iOS & Android)",
    ],
    compareAi: ["Quick-start quotes", "SvendAI chat agent", "AI rewriting", "Automatic email sending", "AI invoice scanning"],
    ctaRowNote: "Free to get started · no commitment",
    choosePro: "Choose Pro", chooseBiz: "Choose Business+",
    perMonth: "/ mo.",
    bandTitle: "Try it free — no commitment",
    bandBody: "Get started today, and upgrade to Pro or Business+ whenever it suits you.",
    seeFeatures: "See all features",
  },
} as const;

function CheckItem({ children, color = "var(--brand)" }: { children: React.ReactNode; color?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 11, fontSize: 15, color: "var(--text-body)" }}>
      <span style={{ flex: "none", marginTop: 1 }}><Icon name="checkmark-circle" size={19} color={color} /></span>
      <span>{children}</span>
    </div>
  );
}

function CompareRow({ label, pro, biz, first }: { label: string; pro: React.ReactNode; biz: React.ReactNode; first?: boolean }) {
  return (
    <div className="ds-compare-grid" style={{ display: "grid", gridTemplateColumns: "1.7fr 1fr 1fr", alignItems: "center", borderTop: first ? undefined : "1px solid var(--hairline)" }}>
      <div className="ds-compare-row-label" style={{ padding: "16px 28px", fontSize: 15, color: "var(--text-body)" }}>{label}</div>
      <div className="ds-compare-cell" style={{ padding: 16, display: "flex", justifyContent: "center", borderLeft: "1px solid var(--hairline)" }}>{pro}</div>
      <div className="ds-compare-cell" style={{ padding: 16, display: "flex", justifyContent: "center", background: "var(--green-50)" }}>{biz}</div>
    </div>
  );
}

const dash = <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-faint)", fontSize: 16 }}>—</span>;
const checkBrand = <Icon name="checkmark" size={20} color="var(--brand)" />;
const checkAccent = <Icon name="checkmark" size={20} color="var(--accent-strong)" />;

export default function PricingContent({ lang = "da" }: { lang?: Lang }) {
  const t = T[lang];
  const home = lang === "en" ? "/en" : "/";
  return (
    <div className={`svendetid-ds ${dsFontClass}`} style={{ background: "#FFFFFF" }}>
      <style>{dsTokensCss}</style>
      <style>{`html,body{background:#FFFFFF;overscroll-behavior-y:none;overflow-x:hidden;overflow-x:clip;}`}</style>

      <LandingNav lang={lang} />

      {/* ===== HERO ===== */}
      <section style={{ padding: "144px 0 8px", textAlign: "center" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "0 32px" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 9, padding: "8px 15px", borderRadius: 999, background: "var(--orange-100)", border: "1px solid var(--orange-200)", marginBottom: 22 }}>
            <Icon name="sparkles" size={15} color="var(--accent-strong)" />
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--accent-ink)" }}>{t.badge}</span>
          </div>
          <h1 className="ds-h1" style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 54, lineHeight: 1.06, letterSpacing: "-0.025em", margin: 0, color: "var(--green-900)", textWrap: "balance" }}>{t.h1}</h1>
          <p style={{ fontSize: 18, lineHeight: 1.6, color: "var(--text-muted)", maxWidth: "34em", margin: "20px auto 0" }}>{t.sub}</p>
        </div>
      </section>

      {/* ===== PRICING CARDS ===== */}
      <section id="planer" style={{ padding: "48px 0 24px" }}>
        <div className="ds-2col" style={{ maxWidth: 920, margin: "0 auto", padding: "0 32px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, alignItems: "start" }}>

          {/* Pro */}
          <div style={{ background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 22, padding: "36px 34px", boxShadow: "0 6px 16px rgba(16,33,27,0.06)", display: "flex", flexDirection: "column" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-muted)" }}>{t.proLabel}</div>
            <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 26, letterSpacing: "-0.015em", margin: "8px 0 0", color: "var(--green-900)" }}>{t.proTitle}</h2>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "22px 0 2px" }}>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 20, color: "var(--text-muted)", alignSelf: "flex-start", marginTop: 8 }}>kr</span>
              <span style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 60, lineHeight: 1, letterSpacing: "-0.02em", color: "var(--green-900)" }}>99</span>
            </div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, letterSpacing: "0.03em", color: "var(--text-muted)" }}>{t.perUser}</div>
            <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-accent" style={{ marginTop: 24, cursor: "pointer", border: "none", width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, background: "var(--accent)", color: "#fff", fontSize: 15, fontWeight: 600, padding: "14px 20px", borderRadius: 11, boxShadow: "0 10px 24px rgba(194,65,12,0.32)" }}>{t.cta}</button></SignUpButton>
            <div style={{ height: 1, background: "var(--hairline)", margin: "28px 0 22px" }} />
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-body)", marginBottom: 16 }}>{t.proIncludes}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
              {t.proItems.map((item) => <CheckItem key={item}>{item}</CheckItem>)}
              <CheckItem><strong style={{ fontWeight: 600 }}>5 GB</strong> {t.storagePro}</CheckItem>
            </div>
          </div>

          {/* Business+ (highlighted) */}
          <div style={{ background: "var(--surface-card)", border: "1.5px solid var(--green-700)", borderRadius: 22, padding: "36px 34px", boxShadow: "0 24px 50px rgba(16,33,27,0.14)", display: "flex", flexDirection: "column", position: "relative" }}>
            <div style={{ position: "absolute", top: -13, left: "50%", transform: "translateX(-50%)", display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 999, background: "var(--accent)", color: "#fff", fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", boxShadow: "0 6px 14px rgba(194,65,12,0.3)", whiteSpace: "nowrap" }}><Icon name="sparkles" size={12} /> {lang === "en" ? "Recommended" : "Anbefalet"}</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--accent-strong)" }}>Business+</div>
            <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 26, letterSpacing: "-0.015em", margin: "8px 0 0", color: "var(--green-900)" }}>{t.bizTitlePrefix}<span style={{ fontStyle: "italic", color: "var(--accent-strong)" }}>SvendAI</span></h2>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "22px 0 2px" }}>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 20, color: "var(--text-muted)", alignSelf: "flex-start", marginTop: 8 }}>kr</span>
              <span style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 60, lineHeight: 1, letterSpacing: "-0.02em", color: "var(--green-900)" }}>199</span>
            </div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, letterSpacing: "0.03em", color: "var(--text-muted)" }}>{t.perUser}</div>
            <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-blue" style={{ marginTop: 24, cursor: "pointer", border: "none", width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 15, fontWeight: 600, padding: "14px 20px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.32)" }}>{t.cta}<Icon name="arrow-forward" size={16} /></button></SignUpButton>
            <div style={{ height: 1, background: "var(--hairline)", margin: "28px 0 22px" }} />
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-body)", marginBottom: 16 }}>{t.bizIncludes}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
              <CheckItem color="var(--accent-strong)"><strong style={{ fontWeight: 600 }}>{t.quickstart}</strong>{t.quickstartBody}</CheckItem>
              {t.bizItems.map((item) => <CheckItem key={item} color="var(--accent-strong)">{item}</CheckItem>)}
              <CheckItem color="var(--accent-strong)">{t.storageBizPrefix}<strong style={{ fontWeight: 600 }}>10 GB</strong>{t.storageBiz}</CheckItem>
            </div>
          </div>
        </div>
        <div style={{ textAlign: "center", marginTop: 22, fontSize: 14, color: "var(--text-muted)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}><Icon name="lock-closed" size={15} color="var(--text-faint)" /> {t.fineprint}</div>
      </section>

      {/* ===== COMPARISON TABLE ===== */}
      <section style={{ padding: "80px 0 96px" }}>
        <div style={{ maxWidth: 920, margin: "0 auto", padding: "0 32px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 36 }}>
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--accent-strong)" }}>{t.compare}</span>
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
          </div>

          <div style={{ background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 20, overflow: "hidden", boxShadow: "0 6px 16px rgba(16,33,27,0.06)" }}>
            {/* header row */}
            <div className="ds-compare-grid" style={{ display: "grid", gridTemplateColumns: "1.7fr 1fr 1fr" }}>
              <div style={{ padding: "24px 28px" }} />
              <div className="ds-compare-head-cell" style={{ padding: "24px 16px", textAlign: "center", borderLeft: "1px solid var(--hairline)" }}>
                <div className="ds-compare-plan-name" style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 18, color: "var(--green-900)" }}>Pro</div>
                <div className="ds-compare-plan-price" style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)", marginTop: 3 }}>kr 99 {t.perMonth}</div>
              </div>
              <div className="ds-compare-head-cell" style={{ padding: "24px 16px", textAlign: "center", background: "var(--green-900)" }}>
                <div className="ds-compare-plan-name" style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 18, color: "#fff" }}>Business+</div>
                <div className="ds-compare-plan-price" style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "#F4C8A6", marginTop: 3 }}>kr 199 {t.perMonth}</div>
              </div>
            </div>

            {/* section: drift */}
            <div className="ds-compare-grid" style={{ display: "grid", gridTemplateColumns: "1.7fr 1fr 1fr", background: "var(--green-50)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
              <div className="ds-compare-section-label" style={{ padding: "11px 28px", fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)" }}>{t.drift}</div>
              <div style={{ borderLeft: "1px solid var(--hairline)" }} />
              <div style={{ background: "var(--green-50)" }} />
            </div>

            {t.compareDrift.map((label, i) => (
              <CompareRow key={label} label={label} pro={checkBrand} biz={checkBrand} first={i === 0} />
            ))}
            <CompareRow
              label={t.storageRow}
              pro={<span style={{ fontFamily: "var(--font-mono)", fontSize: 14, fontWeight: 500, color: "var(--text-body)" }}>5 GB</span>}
              biz={<span style={{ fontFamily: "var(--font-mono)", fontSize: 14, fontWeight: 500, color: "var(--brand-strong)" }}>10 GB</span>}
            />

            {/* section: SvendAI */}
            <div className="ds-compare-grid" style={{ display: "grid", gridTemplateColumns: "1.7fr 1fr 1fr", background: "var(--green-50)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
              <div className="ds-compare-section-label" style={{ padding: "11px 28px", fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--accent-strong)", display: "flex", alignItems: "center", gap: 7 }}><Icon name="sparkles" size={12} /> SvendAI</div>
              <div style={{ borderLeft: "1px solid var(--hairline)" }} />
              <div />
            </div>

            {t.compareAi.map((label, i) => (
              <CompareRow key={label} label={label} pro={dash} biz={checkAccent} first={i === 0} />
            ))}

            {/* CTA row */}
            <div className="ds-compare-grid" style={{ display: "grid", gridTemplateColumns: "1.7fr 1fr 1fr", alignItems: "center", borderTop: "1px solid var(--border)" }}>
              <div className="ds-compare-cta-note" style={{ padding: "22px 28px", fontSize: 13, color: "var(--text-muted)" }}>{t.ctaRowNote}</div>
              <div className="ds-compare-cta-cell" style={{ padding: "18px 16px", display: "flex", justifyContent: "center", borderLeft: "1px solid var(--hairline)" }}>
                <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-soft" style={{ cursor: "pointer", border: "1px solid var(--border)", fontSize: 14, fontWeight: 600, color: "var(--green-900)", padding: "10px 16px", borderRadius: 9, background: "transparent" }}>{t.choosePro}</button></SignUpButton>
              </div>
              <div className="ds-compare-cta-cell" style={{ padding: "18px 16px", display: "flex", justifyContent: "center", background: "var(--green-50)" }}>
                <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-accent" style={{ cursor: "pointer", border: "none", fontSize: 14, fontWeight: 600, color: "#fff", background: "var(--accent)", padding: "10px 16px", borderRadius: 9, boxShadow: "0 6px 14px rgba(194,65,12,0.28)" }}>{t.chooseBiz}</button></SignUpButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA BAND ===== */}
      <section style={{ padding: "0 0 96px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 32px" }}>
          <div style={{ background: "var(--green-900)", borderRadius: 26, padding: "64px 48px", textAlign: "center" }}>
            <img src="/svendetid-logo.png" alt="" style={{ width: 64, height: 64, margin: "0 auto 18px", display: "block", opacity: 0.96 }} />
            <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, letterSpacing: "-0.02em", lineHeight: 1.08, margin: "0 auto", maxWidth: "18em", color: "#fff" }}>{t.bandTitle}</h2>
            <p style={{ fontSize: 17, lineHeight: 1.6, color: "var(--text-hero-muted)", maxWidth: "30em", margin: "16px auto 0" }}>{t.bandBody}</p>
            <div className="ds-cta-row" style={{ display: "flex", flexWrap: "wrap", gap: 14, justifyContent: "center", marginTop: 32 }}>
              <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-blue" style={{ cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 26px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.cta}<Icon name="arrow-forward" size={18} /></button></SignUpButton>
              <Link href={lang === "en" ? "/en/features" : "/features"} className="btn-ghost-dark" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, border: "1px solid rgba(255,255,255,0.24)" }}>{t.seeFeatures}</Link>
            </div>
          </div>
        </div>
      </section>

      <Footer lang={lang} daHref="/pricing" enHref="/en/pricing" />
    </div>
  );
}
